import { useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ComboBox, InputWithLabel, Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import routes from '@/Router/routes.constants'

import AddReturnSteps from './AddReturnSteps/AddReturnSteps'
import useReturnInstructionSet from './hooks/useReturnInstructionSet/useReturnInstructionSet'
import ReturnInstructionDetailConfigurationSkeleton from './ReturnInstructionDetailConfigurationSkeleton/ReturnInstructionDetailConfigurationSkeleton'
import ReturnInstructionUses from './ReturnInstructionUses/ReturnInstructionUses'
import { typeOptions } from './constants'
import * as Styles from './styles'

const ReturnInstructionDetailConfiguration = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { id } = useParams()
  const navigate = useNavigate()
  const supportedLanguages = info?.selectedStore?.supported_languages
  const { regions } = useRegions()
  const { hasPermission } = useUserPermissions()
  const isEditable = hasPermission(userPermissions.editLogistics)

  const {
    name,
    setName,
    type,
    setType,
    steps,
    onAddStep,
    onRemoveStep,
    onChangeStep,
    hasChanges,
    isFormValid,
    onSaveChanges,
    revertChanges,
    creating,
    editing,
    fetching,
    deliveryMethods,
    languages,
    isNew,
  } = useReturnInstructionSet({
    id,
    storeId: info?.selectedStore?.id,
    supportedLanguages,
    setToastInfo,
  })

  const handleChangeName = e => {
    setName(e.target.value)
  }

  const handleChangeType = e => {
    setType(e.target.value)
  }

  const translatedTypeOptions = typeOptions.map(option => ({ ...option, label: t(option.label) }))

  const handleSelectDeliveryMethod = method => {
    navigate(routes.configurationPage.logistics.deliveryMethodDetailConfiguration.replace(':id', method.id), {
      state: { childPageTitle: method.internal_name, selectedDeliveryMethod: method },
    })
  }

  if (fetching) return <ReturnInstructionDetailConfigurationSkeleton />

  return (
    <>
      <Styles.MainContainer>
        {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
        <Styles.SectionContainer>
          <Text type="titleS">
            <Trans i18nKey="returnInstructionName">Nombre</Trans>
          </Text>
          <InputWithLabel
            label={t('Nombre interno')}
            value={name}
            onChange={handleChangeName}
            disabled={!isEditable}
            fullWidth
          />
        </Styles.SectionContainer>
        <Styles.SectionContainer>
          <Text type="titleS">{t('Tipo')}</Text>
          <ComboBox
            label={t('Tipo de devolución')}
            value={type}
            handleChange={handleChangeType}
            menuItems={translatedTypeOptions}
            disabled={!isNew || !isEditable}
            fullWidth
          />
        </Styles.SectionContainer>
        <AddReturnSteps
          steps={steps}
          onAdd={onAddStep}
          onRemove={onRemoveStep}
          onChange={onChangeStep}
          languages={languages}
          disabled={!isEditable}
        />
        {!isNew && (
          <ReturnInstructionUses
            deliveryMethods={deliveryMethods}
            regions={regions}
            onSelectDeliveryMethod={handleSelectDeliveryMethod}
          />
        )}
      </Styles.MainContainer>
      <Styles.ActionsContainer>
        <SaveChangesButtons
          loading={creating || editing}
          disabled={!isFormValid() || creating || editing}
          onSave={onSaveChanges}
          hasChanges={isEditable && hasChanges}
          onRevertChanges={revertChanges}
        />
      </Styles.ActionsContainer>
    </>
  )
}

export default ReturnInstructionDetailConfiguration
